import { MouseEvent, SyntheticEvent, useState } from 'react';

import { useIsAllowedPermission } from '@mint-lib/profile-helpers';

import { BUILDER_ROW_TYPES } from '../../../../../../constants/campaignBuilder.js';
import { useCellValueUpdate } from '../../../../../../hooks/useCellValueUpdate.js';
import { TRACKING_EVENTS } from '../../../../../../types/performanceTracking.js';
import { getSelectedOption } from '../../../../../../utils/autocomplete.js';
import {
  BuilderTableColumn,
  BuilderTableRow,
  ColumnOptionItem,
} from '../../../../types.js';

type UseSelectableFieldArgs = {
  value: string | null;
  column: BuilderTableColumn;
  row: BuilderTableRow;
};

const useSelectableValueField = ({
  value,
  column,
  row,
}: UseSelectableFieldArgs) => {
  const selectedOption = getSelectedOption(value, column.options);

  const [autocompleteInputValue, setAutocompleteInputValue] = useState(
    selectedOption.displayName,
  );

  const isAllowed = useIsAllowedPermission('campaignPlanningFeature:modify');

  const [isFieldFocused, setIsFieldFocused] = useState<boolean>(false);
  const { startMeasurePerformance } = column;
  const {
    type,
    isBuyingPlanLocked,
    isCampaignCurrencyMode,
    isPredictivePlanningMode,
    isArchivedVersion,
  } = row.original;

  const { updateCellValue } = useCellValueUpdate({
    row,
    column,
  });

  const isTileRow = type === BUILDER_ROW_TYPES.TILE;
  const isEmptyValue = !value || value === '';
  const isReadOnlyField =
    !isTileRow ||
    isPredictivePlanningMode ||
    !isAllowed ||
    isArchivedVersion ||
    isCampaignCurrencyMode ||
    isBuyingPlanLocked;
  const isResetBtnShown = !isEmptyValue && !isReadOnlyField;

  const handleOpenMenu = () => {
    if (isReadOnlyField) {
      return;
    }

    setIsFieldFocused(true);
  };

  const handleCloseMenu = () => {
    setIsFieldFocused(false);
  };

  const handleAutocompleteInputValueChange = (
    e: SyntheticEvent,
    newInputValue: string,
  ) => {
    if (!e) {
      return;
    }

    setAutocompleteInputValue(newInputValue);
  };

  const handleChangeValue = async (
    e: SyntheticEvent,
    option: ColumnOptionItem | null,
  ) => {
    if (!option) {
      return;
    }

    if (option.value !== value) {
      startMeasurePerformance(TRACKING_EVENTS.TABLE_UPDATE);

      await updateCellValue({
        currentValue: value,
        newValue: option.value,
      });
    }
  };

  const handleResetValue = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    startMeasurePerformance(TRACKING_EVENTS.TABLE_UPDATE);

    await updateCellValue({
      currentValue: value,
      newValue: null,
    });

    setAutocompleteInputValue('');
  };

  return {
    selectedOption,
    autocompleteInputValue,
    isFieldFocused,
    isReadOnlyField,
    isResetBtnShown,
    handleOpenMenu,
    handleCloseMenu,
    handleChangeValue,
    handleResetValue,
    handleAutocompleteInputValueChange,
  };
};

export default useSelectableValueField;
